// 각성 연출(COMBAT-FEEL 5장): 모으기 붉은 틴트·비네트·집중선 → 섬광(리미터 경유) → 컷인 2D 패널 → 난무 큰 참격선 → 복귀 잔상.
// 전체 화면 섬광은 Flash 단계 1회 + AWAKEN.fullFlashSlashes 참격 1회. 섬광 약화 설정이면 회색으로 약하게.
import { AWAKEN, type QualityLevel } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import { AwakenPhase, type GameView } from '../shared/types';
import type { GameClocks } from '../render/clock';
import type { PostFx } from '../render/post';
import { clamp01, easeOut, phaseOf } from '../render/util';
import { PRIORITY_WAIT_MS, type FlashLimiter } from './flash';
import type { SlashFx } from './slashes';
import { assetUrl } from '../pwa/assetMap';

/** 60Hz 한 틱(ms) */
const TICK_MS = 1000 / 60;

const PHASE_TOTAL = [0, AWAKEN.gather, AWAKEN.flash, AWAKEN.cutin, AWAKEN.barrage, AWAKEN.recover];
/** 컷인 얼굴(승인 콘셉트 c-awaken-cutin에서 자른 손그림). 읽지 못하면 아래 실루엣이 그대로 보인다. */
const PORTRAIT_URL = assetUrl('ui/kael_cutin.webp');
const SILHOUETTE =
  '<svg viewBox="0 0 200 220" width="100%" height="100%" preserveAspectRatio="xMidYMax meet"><g fill="#14040a">' +
  '<path d="M100 18 l12 22 l18 -14 l-4 24 l24 -6 l-14 20 l20 6 l-22 10 q6 30 -10 46 l-24 0 q-16 -16 -10 -46 l-22 -10 l20 -6 l-14 -20 l24 6 l-4 -24 l18 14 z"/>' +
  '<path d="M52 220 q4 -64 48 -74 q44 10 48 74 z"/>' +
  '<rect x="138" y="40" width="10" height="150" transform="rotate(28 143 115)"/></g>' +
  '<circle cx="90" cy="92" r="4" fill="#ff2a3d"/><circle cx="112" cy="92" r="4" fill="#ff2a3d"/></svg>';
/**
 * 컷인 띠 위 참격·파편(정적 SVG, 깜빡이지 않는다): 끝이 가늘어지는 들쭉날쭉한 진홍 참격 + 흰 코어, 검은 돌 파편, 드문 불티.
 * 콘셉트 c-awaken-cutin의 "날카로운 사선 참격"을 규칙적인 줄무늬 대신 손으로 그린 모양으로 낸다.
 */
const SLASH_SVG = (() => {
  // 참격 한 줄: 시작점·끝점·최대 두께. 가운데가 두껍고 양끝이 뾰족하며 한쪽에 톱니가 있다
  const slash = (x0: number, y0: number, x1: number, y1: number, w: number, core: number, bend = 0): string => {
    const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy), nx = -dy / len, ny = dx / len;
    const pts: string[] = [], back: string[] = [];
    const N = 13;
    for (let i = 0; i <= N; i++) {
      // 휘어진 초승달 획: 가운데가 bend만큼 볼록하고, 바깥 가장자리는 톱니(찢긴 끝), 끝으로 갈수록 가늘다
      const t = i / N, taper = Math.sin(Math.PI * t) ** 0.7, jag = i % 3 === 1 ? 0.45 : i % 3 === 2 ? 0.8 : 1;
      const arc = Math.sin(Math.PI * t) * bend;
      const cx = x0 + dx * t + nx * arc, cy = y0 + dy * t + ny * arc;
      pts.push((cx + nx * w * taper * jag).toFixed(1) + ',' + (cy + ny * w * taper * jag).toFixed(1));
      back.unshift((cx - nx * w * 0.45 * taper).toFixed(1) + ',' + (cy - ny * w * 0.45 * taper).toFixed(1));
    }
    const c = (k: number) => (x0 + dx * k + nx * Math.sin(Math.PI * k) * bend).toFixed(1) + ',' + (y0 + dy * k + ny * Math.sin(Math.PI * k) * bend).toFixed(1);
    const mid = (s: number) => ((x0 + dx * 0.5 + nx * bend) + nx * s).toFixed(1) + ',' + ((y0 + dy * 0.5 + ny * bend) + ny * s).toFixed(1);
    // 세 겹: 밝은 진홍 몸통(띠 진홍과 구분되게) → 옅은 분홍 속 → 굵은 흰 코어
    return '<polygon points="' + pts.concat(back).join(' ') + '" fill="#ff2844"/>' +
      '<polygon points="' + c(0.05) + ' ' + c(0.28) + ' ' + mid(w * 0.55) + ' ' + c(0.74) + ' ' + c(0.96) + ' ' + mid(-w * 0.25) + '" fill="#ffa3b0"/>' +
      '<polygon points="' + c(0.08) + ' ' + c(0.3) + ' ' + mid(core * 1.6) + ' ' + c(0.72) + ' ' + c(0.94) + ' ' + mid(-core * 0.6) + '" fill="#fff"/>';
  };
  // 돌 파편: 어두운 면 + 빛 받는 면(두 톤)으로 입체감, 테두리에 붉은 반사
  const shard = (x: number, y: number, s: number, r: number): string =>
    '<g transform="translate(' + x + ' ' + y + ') rotate(' + r + ')">' +
    '<polygon points="0,-' + s + ' ' + (s * 0.7).toFixed(1) + ',' + (s * 0.2).toFixed(1) + ' ' + (s * 0.1).toFixed(1) + ',' + s + ' -' + (s * 0.6).toFixed(1) + ',' + (s * 0.3).toFixed(1) + '" fill="#160205" stroke="#ff3a4c" stroke-width="1.2" stroke-opacity=".6"/>' +
    '<polygon points="0,-' + s + ' ' + (s * 0.7).toFixed(1) + ',' + (s * 0.2).toFixed(1) + ' ' + (s * 0.15).toFixed(1) + ',' + (s * 0.05).toFixed(1) + '" fill="#5a1a1e"/></g>';
  const spark = (x: number, y: number, r: number): string => '<circle cx="' + x + '" cy="' + y + '" r="' + r + '" fill="#ffd9de"/>';
  // 띠의 화면 비율은 약 5:1(가로 130%·세로 46%, 16:9)이다. viewBox도 1500×300(5:1)으로 맞춰 획 두께가 한쪽으로 눌리지 않게 한다(가로 좌표만 1.5배).
  const X = 1.5;
  return '<svg viewBox="0 0 1500 300" width="100%" height="100%" preserveAspectRatio="none" style="position:absolute;inset:0;filter:drop-shadow(0 0 6px #ff2a3d)">' +
    // 얼굴(띠 오른쪽 40%)은 가리지 않는다: 왼쪽~가운데에서 엇갈리는 두 큰 초승달 참격 + 보조 획, 얼굴 아래 가장자리 한 줄
    slash(-10 * X, 285, 640 * X, 60, 22, 5.5, -46) + slash(40 * X, 40, 600 * X, 290, 15, 3.6, 36) + slash(250 * X, 20, 600 * X, -5, 7, 1.8, -12) + slash(560 * X, 300, 1010 * X, 250, 8, 2, 10) +
    shard(300 * X, 200, 22, 20) + shard(360 * X, 140, 15, 75) + shard(240 * X, 95, 17, 140) + shard(470 * X, 235, 19, 200) + shard(520 * X, 115, 12, 300) + shard(180 * X, 235, 14, 250) + shard(410 * X, 70, 10, 30) + shard(120 * X, 160, 11, 110) +
    spark(330 * X, 120, 2.6) + spark(410 * X, 175, 2) + spark(280 * X, 160, 1.8) + spark(560 * X, 205, 2.4) + spark(615 * X, 70, 1.8) +
    '</svg>';
})();

export class AwakenFx {
  private readonly cutin: HTMLDivElement;
  private readonly band: HTMLDivElement;
  private tint = 0;
  private vignette = 0;
  private lines = 0;
  private chroma = 0;
  private chromaKick = 0;
  private cutinShown = false;
  /** 컷인 패널 위치(0.5% 단위로 양자화해 바뀔 때만 쓴다) */
  private lastBandX = Number.NaN;
  /** 영웅 림에 더할 세기 */
  rimBoost = 0;
  /** 지금 영웅(c022). 라이라는 진홍 룬·참격선을 그리지 않고(화살비는 LyraFx), 컷인·틴트를 청록으로 바꾼다 */
  private hero: 'kael' | 'lyra' = 'kael';
  private readonly sil: HTMLDivElement;
  private readonly face: HTMLDivElement;
  private readonly label: HTMLDivElement;
  private readonly slashLines: HTMLDivElement;

  constructor(
    layer: HTMLElement,
    private readonly post: PostFx,
    private readonly slashes: SlashFx,
    private readonly flash: FlashLimiter,
    private readonly view: GameView,
    quality: QualityLevel,
  ) {
    void quality;
    this.cutin = document.createElement('div');
    this.cutin.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;display:none;';
    this.band = document.createElement('div');
    this.band.style.cssText =
      'position:absolute;left:-15%;right:-15%;top:20%;height:46%;transform:translateX(-120%) skewY(-9deg);will-change:transform;' +
      // 검붉은 띠(콘셉트 c-awaken-cutin): 분홍으로 뜨지 않게 가운데도 진홍까지만 올리고, 흰 테두리 + 안쪽 붉은 광
      'background:linear-gradient(90deg,#070002 0%,#3d0007 24%,#a50d1d 56%,#3d0007 82%,#070002 100%);' +
      'border-top:4px solid #fff;border-bottom:4px solid #fff;box-shadow:0 0 36px rgba(255,42,61,.75),inset 0 0 46px rgba(255,42,61,.55);overflow:hidden;';
    const lines = document.createElement('div');
    // 들쭉날쭉한 참격·파편·불티(SVG). 규칙적인 줄무늬는 쓰지 않는다(dream-loop 컷인 2회차)
    lines.style.cssText = 'position:absolute;inset:0;';
    lines.innerHTML = SLASH_SVG;
    const sil = document.createElement('div');
    sil.style.cssText = 'position:absolute;right:16%;bottom:-6%;width:34%;height:118%;transform:skewY(9deg);';
    sil.innerHTML = SILHOUETTE;
    // 얼굴 클로즈업(COMBAT-FEEL 5장 컷인): 실루엣 위에 겹치고, 왼쪽 가장자리를 띠 색으로 녹인다
    const face = document.createElement('div');
    // 첫 각성에서 늦게 뜨지 않게 생성 시 한 번 미리 받아 둔다
    new Image().src = PORTRAIT_URL;
    face.style.cssText =
      'position:absolute;right:8%;top:-14%;width:44%;height:128%;transform:skewY(9deg);' +
      "background:url('" + PORTRAIT_URL + "') center 30%/cover no-repeat;" +
      '-webkit-mask-image:linear-gradient(90deg,transparent 0%,#000 22%,#000 82%,transparent 100%);' +
      'mask-image:linear-gradient(90deg,transparent 0%,#000 22%,#000 82%,transparent 100%);';
    const label = document.createElement('div');
    label.style.cssText = 'position:absolute;left:18%;top:28%;transform:skewY(9deg);font:900 italic 64px Impact,"Arial Black","Malgun Gothic",sans-serif;color:#fff;-webkit-text-stroke:3px #5a0010;text-shadow:0 5px 0 #5a0010,0 0 24px rgba(255,255,255,.6);letter-spacing:2px;';
    this.sil = sil; this.face = face; this.label = label; this.slashLines = lines;
    label.innerHTML = '카엘<div style="font-size:30px;letter-spacing:6px;margin-top:4px;-webkit-text-stroke:1px #5a0010;color:#ffe7ea">각성 · 붉은 난무</div>';
    this.band.append(sil, face, lines, label);
    this.cutin.appendChild(this.band);
    layer.appendChild(this.cutin);
  }

  /**
   * 영웅 바꾸기(c022, 렌더가 매 프레임 view.heroId로 부른다. 바뀔 때만 DOM·uniform을 고친다).
   * 라이라: 청록 컷인 띠(얼굴 그림·카엘 실루엣·진홍 참격 숨김), 화면 틴트 청록. 카엘: 원래 값 그대로
   */
  setHero(hero: 'kael' | 'lyra'): void {
    if (hero === this.hero) return;
    this.hero = hero;
    const lyra = hero === 'lyra';
    this.band.style.background = lyra
      ? 'linear-gradient(90deg,#00070a 0%,#06302e 24%,#1f8f86 56%,#06302e 82%,#00070a 100%)'
      : 'linear-gradient(90deg,#070002 0%,#3d0007 24%,#a50d1d 56%,#3d0007 82%,#070002 100%)';
    this.band.style.boxShadow = lyra ? '0 0 36px rgba(63,208,192,.7),inset 0 0 46px rgba(63,208,192,.5)' : '0 0 36px rgba(255,42,61,.75),inset 0 0 46px rgba(255,42,61,.55)';
    this.sil.style.display = this.face.style.display = this.slashLines.style.display = lyra ? 'none' : '';
    this.label.style.webkitTextStroke = lyra ? '3px #06302e' : '3px #5a0010';
    this.label.style.textShadow = lyra ? '0 5px 0 #06302e,0 0 24px rgba(200,210,220,.6)' : '0 5px 0 #5a0010,0 0 24px rgba(255,255,255,.6)';
    this.label.innerHTML = lyra
      ? '라이라<div style="font-size:30px;letter-spacing:6px;margin-top:4px;-webkit-text-stroke:1px #06302e;color:#e6fbf8">각성 · 폭풍 화살비</div>'
      : '카엘<div style="font-size:30px;letter-spacing:6px;margin-top:4px;-webkit-text-stroke:1px #5a0010;color:#ffe7ea">각성 · 붉은 난무</div>';
    if (lyra) this.post.tintColor.value.setRGB(0.25, 0.85, 0.78);
    else this.post.tintColor.value.setRGB(1.0, 0.3, 0.34);
  }

  private flashStrength(): number {
    return this.view.settings.reduceFlash ? 0.5 : 1;
  }

  onEvent(e: GameEvent, clocks: GameClocks): void {
    if (e.type === Ev.AwakenPhase) {
      // 모으기: 발밑 진홍 룬 서클(라이브러리 runeAwaken, 2.4초)
      if (e.a === AwakenPhase.Gather) {
        if (this.hero === 'kael') this.slashes.sink?.awakenGather(this.view.hero.x, this.view.hero.z);
        // c015: 모으기 뒤 섬광 단계의 전체 섬광이 곧 온다 — 그동안 옅은 스킬 화면 펄스가 공용 예산을 쓰지 않게 예약
        this.flash.reserve(clocks.realMs + AWAKEN.gather * TICK_MS + PRIORITY_WAIT_MS);
      }
      if (e.a === AwakenPhase.Flash) {
        // 우선 섬광: 예산에 막히면 대기했다가 건다. 이어지는 난무의 전체 섬광 참격(6번째)까지 예약을 늘린다
        this.flash.request(clocks.realMs, AWAKEN.flash * TICK_MS, this.flashStrength(), 0, true);
        this.flash.reserve(clocks.realMs + (AWAKEN.flash + AWAKEN.cutin + AWAKEN.barrage) * TICK_MS + PRIORITY_WAIT_MS);
      }
    } else if (e.type === Ev.AwakenSlash) {
      const idx = e.a | 0;
      // 라이라 폭풍 화살비 한 파는 LyraFx(lyra_awaken_rain)가 그린다. 섬광 예산·색수차 반동은 같이 쓴다
      if (this.hero === 'kael') this.slashes.awakenLine(e.x, e.z, e.b, AWAKEN.radius, clocks.heroAt(e.tick));
      this.chromaKick = 1;
      const full = AWAKEN.fullFlashSlashes as readonly number[];
      if (full.includes(idx)) {
        this.flash.request(clocks.realMs, 50, this.flashStrength(), 0, true);
        // 마지막 전체 섬광 참격을 요청했으면 예약을 푼다(대기 중이면 대기 섬광이 계속 옅은 펄스를 막는다)
        if (idx === full[full.length - 1]) this.flash.clearReserve();
      }
    }
  }

  update(alpha: number, clocks: GameClocks, frameDt: number): void {
    const aw = this.view.awaken;
    const phase = aw.phase;
    const total = Number.isFinite(aw.phaseTotal) ? aw.phaseTotal : PHASE_TOTAL[phase] ?? 0;
    const p = total > 0 ? clamp01(phaseOf(aw.phaseTick, 1, alpha) / total) : 1;
    // bloom: 블룸 배율. 컷인·난무는 덧셈 참격 6개가 겹쳐 블룸이 화면 가운데를 분홍·흰 막으로 덮었다(프레임 분리 캡처) → 0.4배
    let tint = 0, vig = 0, lines = 0, chroma = 0, rim = 0, bloom = 1;
    switch (phase) {
      case AwakenPhase.Gather:
        tint = 0.6 * easeOut(p); vig = 0.75 * p; lines = p; chroma = 0.004 * p; rim = 1.2 * p;
        break;
      case AwakenPhase.Flash:
        tint = 0.55; vig = 0.7; lines = 1; chroma = 0.006; rim = 1.4;
        break;
      case AwakenPhase.Cutin:
        // 컷인 뒤 전장은 검붉게 가라앉혀 띠가 뜨게 한다(분홍 안개 금지)
        tint = 0.75; vig = 0.95; lines = 0; rim = 1.4; bloom = 0.4;
        break;
      case AwakenPhase.Barrage:
        // 난무: 흰 집중선을 줄이고(분홍빛 번짐의 원인) 검붉은 대비를 남긴다
        tint = 0.42; vig = 0.7; lines = 0.06; chroma = 0.003; rim = 1.2; bloom = 0.4;
        break;
      case AwakenPhase.Recover:
        tint = 0.42 * (1 - p); vig = 0.7 * (1 - p); lines = 0.06 * (1 - p); rim = 1.2 * (1 - p); bloom = 0.4 + 0.6 * p;
        break;
      default:
        break;
    }
    // 각성이 아닐 때는 부드럽게 0으로 돌아간다
    const k = phase === AwakenPhase.None ? Math.min(1, frameDt * 8) : 1;
    this.tint += (tint - this.tint) * k;
    this.vignette += (vig - this.vignette) * k;
    this.lines += (lines - this.lines) * k;
    this.chroma += (chroma - this.chroma) * k;
    this.chromaKick = Math.max(0, this.chromaKick - frameDt * 8);
    const reduce = this.view.settings.reduceFlash;
    this.post.tint.value = this.tint;
    this.post.vignette.value = this.vignette;
    this.post.lines.value = this.lines;
    this.post.chroma.value = (this.chroma + this.chromaKick * 0.006) * (reduce ? 0.5 : 1);
    this.post.setBloomScale(Math.round(bloom * 20) / 20);
    const fl = this.flash.value(clocks.realMs);
    this.post.flash.value = fl;
    if (reduce) this.post.flashColor.value.setRGB(0.55, 0.55, 0.55);
    else if (this.flash.tag === 1) this.post.flashColor.value.setRGB(1, 0.9, 0.62); // 처형 섬광: 흰금색
    else this.post.flashColor.value.setRGB(1, 1, 1);
    this.rimBoost = rim;

    // 컷인 패널: 0~25% 들어오고, 80~100% 나간다(transform만 바꾼다)
    const showCutin = phase === AwakenPhase.Cutin && total > 0;
    if (showCutin !== this.cutinShown) {
      this.cutinShown = showCutin;
      this.cutin.style.display = showCutin ? 'block' : 'none';
      this.lastBandX = Number.NaN;
    }
    if (showCutin) {
      const x = p < 0.25 ? -120 * (1 - easeOut(p / 0.25)) : p > 0.8 ? 120 * ((p - 0.8) / 0.2) : 0;
      const q = Math.round(x * 2) / 2;
      if (q !== this.lastBandX) {
        this.lastBandX = q;
        this.band.style.transform = 'translateX(' + q + '%) skewY(-9deg)';
      }
    }
  }

  reset(): void {
    this.tint = this.vignette = this.lines = this.chroma = this.chromaKick = 0;
    this.cutinShown = false;
    this.cutin.style.display = 'none';
    this.flash.reset();
  }

  /** 미리 그리기용: 컷인 DOM을 한 번 배치해 둔다(레이아웃 비용 선지불). */
  prewarm(): void {
    this.cutin.style.display = 'block';
    void this.band.offsetWidth;
    this.cutin.style.display = 'none';
  }

  dispose(): void {
    this.cutin.remove();
  }
}
